/**
 * analytics.js (r6) — consent-gated product analytics + ad attribution.
 *
 *   PostHog   (VITE_POSTHOG_KEY, VITE_POSTHOG_HOST)  ← requires consent.analytics
 *   Meta Pixel (VITE_META_PIXEL_ID)                   ← requires consent.marketing
 *
 * Consent is the DPDP banner's decision (CookieConsent.jsx → localStorage 'snt_consent_v1').
 * Nothing loads before consent; revoking consent stops capture and clears PostHog's persisted state.
 * Every public function is safe to call at any time (no-ops when not initialised) — call sites never branch on config.
 *
 *   initAnalytics()                 — main.jsx, once
 *   trackPageView(path)             — router listener
 *   track(event, props)             — product events (booking_started, payment_succeeded, …)
 *   identify(user) / reset()        — login / logout
 *   trackPurchase({ value, currency, bookingId }) — Meta 'Purchase' + PostHog 'payment_succeeded'
 *   setOrganizerTracking({ meta_pixel_id, ga4_measurement_id }) / clearOrganizerTracking()   — r7.7 (C6): the event's
 *       own pixel (+ GA4 property) beside the platform pixel, consent-gated the same way; the funnel events below go to
 *       both (fbq trackSingle on the organizer's id; gtag on the organizer's property) while an event page is open.
 */
const CONSENT_KEY = 'snt_consent_v1';
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID;

let posthog = null;          // loaded lazily
let pixelLoaded = false;
let lastConsent = null;
let orgTracking = null;          // r7.7: { meta_pixel_id, ga4_measurement_id } of the event page on screen
const orgPixelsInit = new Set(); // pixel ids already fbq('init')-ed
let gtagLoaded = false;

export function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return { analytics: false, marketing: false, decided: false };
    const c = JSON.parse(raw);
    return { analytics: !!c.analytics, marketing: !!c.marketing, decided: true };
  } catch { return { analytics: false, marketing: false, decided: false }; }
}

async function loadPostHog() {
  if (posthog || !POSTHOG_KEY) return posthog;
  try {
    const mod = await import('posthog-js');
    const ph = mod.default || mod.posthog || mod;
    ph.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: false,          // we send page views from the router (SPA)
      capture_pageleave: true,
      persistence: 'localStorage+cookie',
      autocapture: true,
      mask_all_text: false,
      session_recording: { maskAllInputs: true },   // never record card/phone fields
      loaded: (p) => { if (import.meta.env.DEV) p.debug(false); },
    });
    posthog = ph;
  } catch (err) {
    console.warn('[analytics] posthog failed to load', err?.message);
  }
  return posthog;
}

function loadMetaPixel() {
  if (pixelLoaded || typeof window === 'undefined') return;
  if (!META_PIXEL_ID && !(orgTracking && orgTracking.meta_pixel_id)) return;   // r7.7: an organizer pixel alone is enough to load the base
  /* Standard Meta base code, inlined so no third-party snippet is pasted into index.html unconditionally */
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  if (META_PIXEL_ID) window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
  pixelLoaded = true;
  applyOrganizerPixel();
}

/** r7.7 (C6): init the organizer's pixel once (per id) and fire its PageView for this event page. */
function applyOrganizerPixel() {
  if (!orgTracking?.meta_pixel_id || !pixelLoaded || !window.fbq) return;
  const id = orgTracking.meta_pixel_id;
  if (!orgPixelsInit.has(id)) { window.fbq('init', id); orgPixelsInit.add(id); }
  window.fbq('trackSingle', id, 'PageView');
}
function orgFb(event, props) {
  if (orgTracking?.meta_pixel_id && pixelLoaded && window.fbq) window.fbq('trackSingle', orgTracking.meta_pixel_id, event, props);
}
function loadGtag(id) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  if (!gtagLoaded) { const s = document.createElement('script'); s.async = true; s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`; document.head.appendChild(s); window.gtag('js', new Date()); gtagLoaded = true; }
  window.gtag('config', id, { send_page_view: true });
}
function orgGa(name, params) { if (orgTracking?.ga4_measurement_id && lastConsent?.marketing && window.gtag) window.gtag('event', name, { send_to: orgTracking.ga4_measurement_id, ...params }); }

export function setOrganizerTracking(tracking) {
  const next = tracking && (tracking.meta_pixel_id || tracking.ga4_measurement_id) ? { meta_pixel_id: tracking.meta_pixel_id || null, ga4_measurement_id: tracking.ga4_measurement_id || null } : null;
  orgTracking = next;
  if (!next || !lastConsent?.marketing) return;
  if (next.meta_pixel_id) { if (!pixelLoaded) loadMetaPixel(); else applyOrganizerPixel(); }
  if (next.ga4_measurement_id) loadGtag(next.ga4_measurement_id);
}
export function clearOrganizerTracking() { orgTracking = null; }
export function organizerTracking() { return orgTracking; }

function unloadMetaPixel() {
  // fbevents cannot be unloaded; revoking consent → stop sending + delete _fbp/_fbc cookies
  if (typeof window !== 'undefined' && window.fbq) window.fbq('consent', 'revoke');
  ['_fbp', '_fbc'].forEach((c) => { document.cookie = `${c}=; Max-Age=0; path=/; domain=${location.hostname}`; document.cookie = `${c}=; Max-Age=0; path=/`; });
  pixelLoaded = false;
}

/** Apply the current consent state. Idempotent; call on init and whenever consent changes. */
export async function applyConsent(consent = readConsent()) {
  lastConsent = consent;
  if (consent.analytics) {
    const ph = await loadPostHog();
    if (ph && ph.has_opted_out_capturing?.()) ph.opt_in_capturing();
  } else if (posthog) {
    posthog.opt_out_capturing();
    try { posthog.reset(true); } catch { /* ignore */ }
  }
  if (consent.marketing) { loadMetaPixel(); if (orgTracking) setOrganizerTracking(orgTracking); } else if (pixelLoaded) unloadMetaPixel();
}

export function initAnalytics() {
  if (typeof window === 'undefined') return;
  applyConsent();
  // CookieConsent dispatches this after every decision; storage event covers other tabs
  window.addEventListener('snt:consent', (e) => applyConsent(e.detail || readConsent()));
  window.addEventListener('storage', (e) => { if (e.key === CONSENT_KEY) applyConsent(); });
}

export function trackPageView(path = location.pathname + location.search) {
  if (posthog && lastConsent?.analytics) posthog.capture('$pageview', { $current_url: location.origin + path });
  if (pixelLoaded && window.fbq) window.fbq('track', 'PageView');
}

export function track(event, props = {}) {
  if (posthog && lastConsent?.analytics) posthog.capture(event, props);
}

export function identify(user) {
  if (!user || !posthog || !lastConsent?.analytics) return;
  posthog.identify(user.id, { role: user.role, city: user.city || undefined });   // no email/phone → no PII in analytics
}

export function reset() {
  if (posthog) posthog.reset();
}

/** Booking funnel helpers — Meta standard events map straight onto ads reporting. */
export function trackViewContent({ eventId, title, category, valuePaise }) {
  track('event_viewed', { event_id: eventId, category });
  const props = { content_ids: [eventId], content_name: title, content_category: category, value: (valuePaise || 0) / 100, currency: 'INR' };
  if (pixelLoaded && window.fbq && META_PIXEL_ID) window.fbq('trackSingle', META_PIXEL_ID, 'ViewContent', props);
  orgFb('ViewContent', props); orgGa('view_item', { currency: 'INR', value: props.value, items: [{ item_id: eventId, item_name: title, item_category: category }] });
}
export function trackInitiateCheckout({ bookingId, valuePaise, seatCount }) {
  track('booking_started', { booking_id: bookingId, seat_count: seatCount, value: (valuePaise || 0) / 100 });
  const props = { value: (valuePaise || 0) / 100, currency: 'INR', num_items: seatCount };
  if (pixelLoaded && window.fbq && META_PIXEL_ID) window.fbq('trackSingle', META_PIXEL_ID, 'InitiateCheckout', props);
  orgFb('InitiateCheckout', props); orgGa('begin_checkout', { currency: 'INR', value: props.value });
}
export function trackPurchase({ bookingId, valuePaise, seatCount }) {
  track('payment_succeeded', { booking_id: bookingId, seat_count: seatCount, value: (valuePaise || 0) / 100 });
  const props = { value: (valuePaise || 0) / 100, currency: 'INR', content_ids: [bookingId], num_items: seatCount };
  if (pixelLoaded && window.fbq && META_PIXEL_ID) window.fbq('trackSingle', META_PIXEL_ID, 'Purchase', props);
  orgFb('Purchase', props); orgGa('purchase', { currency: 'INR', value: props.value, transaction_id: bookingId });
}

export default { initAnalytics, applyConsent, readConsent, trackPageView, track, identify, reset, trackViewContent, trackInitiateCheckout, trackPurchase, setOrganizerTracking, clearOrganizerTracking, organizerTracking };
